import { useEffect, useRef } from 'react';

/**
 * Who currently holds work that reloading would destroy (021 FR-012, FR-013).
 *
 * FR-013 lets the application apply a pending version BY ITSELF at the next navigation, so that a
 * practitioner who ignores the update offer cannot end up running a stale bundle for days. FR-012
 * forbids that from costing them anything they have typed. This is the fact that reconciles the
 * two, and it is a fact no single owner has:
 *
 *   - 016's action-row draft lives in component state inside the ROW, deliberately, so that a list
 *     re-read cannot reset it. No store can see it.
 *   - 020's import commit is in flight inside a page, and reloading during it is that feature's
 *     *cancel that cannot cancel*: nothing cancels the POST, the actions are created, the answer
 *     is discarded, and the practitioner is shown an empty page for an import that really happened.
 *
 * So the components that hold the work declare it, and this collects the declarations.
 *
 * A Set of holder keys rather than a counter. A row that goes dirty, clean, dirty registers twice;
 * with a counter one release would leave it at 1 and the application could never update again —
 * a permanent block produced by ordinary typing, and invisible until someone wondered why the
 * offer never applied itself.
 *
 * **034: a module, not an injectable, and the Set is module-private.** Angular's root injector gave
 * one instance per application; a module holds one per bundle, which is the same guarantee reached
 * a different way. Nothing exports the Set, so the only ways to change it are `register`/`release`
 * — the same seam the class's private field gave. Pulled forward from Phase 5 because
 * `action-editor` and `quick-add` declare through it and neither can be ported without it.
 */
const holders = new Set<string>();

/** Called by a component while it holds work a reload would destroy. Idempotent per key. */
export function registerUnsavedWork(key: string): void {
  holders.add(key);
}

/** Called on save or on teardown. Silent for a key that never registered — a teardown that throws is a broken teardown. */
export function releaseUnsavedWork(key: string): void {
  holders.delete(key);
}

/** What FR-013's backstop asks before applying a pending version without being told to. */
export function isUnsavedWorkEmpty(): boolean {
  return holders.size === 0;
}

/** Test seam, like the stores'. Production code never calls it. */
export function resetUnsavedWork(): void {
  holders.clear();
}

/**
 * Declare that this component holds work while `isDirty` is true — registering, releasing, and
 * releasing again on teardown, in one call.
 *
 * The teardown half is the load-bearing one and the easy one to forget: a component destroyed
 * while dirty leaves a holder nothing can ever release, which blocks every future self-applied
 * update SILENTLY and permanently. Stating it by hand at each site means the third declarer has to
 * remember a rule whose violation has no symptom; here they get it by calling the hook.
 */
export function useUnsavedWork(key: string, isDirty: boolean): void {
  /**
   * The key is captured ONCE, on the first render, and never re-read.
   *
   * Angular's version cached it because reading it inside a tracked effect would register a new key
   * and never release the old one — and the teardown, which releases the CURRENT key, would not
   * release it either. React's hazard is the same and arrives by a different route: an unmount
   * cleanup closes over the key from the render that created it, so a changed key would leak the
   * original. Captured once, the whole class is unrepresentable, and both callers' keys are derived
   * from an id that is stable for the life of the instance anyway.
   */
  const held = useRef(key);

  useEffect(() => {
    if (isDirty) registerUnsavedWork(held.current);
    else releaseUnsavedWork(held.current);
  }, [isDirty]);

  // Separate effect with an empty dependency list: this cleanup must run on UNMOUNT and never when
  // `isDirty` changes. Folded into the effect above it would release on every toggle, which the
  // register/release pair already does correctly — and would then re-register, which it would not.
  useEffect(() => () => releaseUnsavedWork(held.current), []);
}
